import { useFieldArray, useFormContext } from 'react-hook-form';
import useFundContext from '@/contexts/Fund/useFundContext';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { SupplierFullDtoIn } from '@/models/in/SupplierFullDtoIn';
import ExistingSupplierPicker from '@/pages/Projects/ExistingSupplierPicker';
import { findSupplierByTaxId } from '@/pages/Projects/existingSupplier';
import { useTranslation } from 'react-i18next';

import { ProjectFormData, emptyExpense, emptySupplier, OTHER_OPTION_ID } from '../schemas/projectFormSchema';
import { ActivityDomainDtoIn } from '@/models/in/ActivityDomainDtoIn';
import { ProjectExpenseFrequency } from '@/models/common/ProjectStatus';
import FileUpload from '@/pages/newSupported/components/FileUpload';
import Button from '@/components/ui/Button';

interface ProjectExpensesStepProps {
  activityDomains: ActivityDomainDtoIn[];
  projectTypeLabel: string;
}

/**
 * Step 2 of 3 — "תיאור ופירוט הוצאות": a repeatable expense block (עלות / תדירות / תוקף),
 * document uploads, and the project's suppliers. Suppliers live here rather than in a step of
 * their own because the task requires them to be entered while the project is created, and each
 * new one is routed to the separate suppliers approval queue on submit.
 */
const ProjectExpensesStep = ({ activityDomains, projectTypeLabel }: ProjectExpensesStepProps) => {
  const { t } = useTranslation();
  const { control, register, watch, setValue, formState: { errors } } = useFormContext<ProjectFormData>();

  const { currentFund } = useFundContext();
  // The suppliers the fund already works with, to pick from instead of retyping (SPR-10302).
  const { data: fundSuppliers } = useGetAuthApi<SupplierFullDtoIn[]>(
    currentFund?.id ? `funds/${currentFund.id}/suppliers` : ''
  );

  /** Fills one supplier row from a supplier the fund already has. */
  const fillSupplier = (index: number, supplier: SupplierFullDtoIn) => {
    const domainId = supplier.activityDomainId ?? (supplier.activityDomainOther ? OTHER_OPTION_ID : null);
    setValue(`suppliers.${index}.supplierId`, supplier.id);
    setValue(`suppliers.${index}.name`, supplier.name, { shouldValidate: true });
    setValue(`suppliers.${index}.taxId`, supplier.taxId, { shouldValidate: true });
    setValue(`suppliers.${index}.activityDomainId`, domainId, { shouldValidate: true });
    setValue(`suppliers.${index}.activityDomainOther`, supplier.activityDomainOther ?? '');
  };

  const expenses = useFieldArray({ control, name: 'expenses' });
  const suppliers = useFieldArray({ control, name: 'suppliers' });
  const documents = useFieldArray({ control, name: 'documents' });

  const expenseErrors = errors.expenses as { amount?: { message?: string } }[] | undefined;
  const supplierErrors = errors.suppliers as {
    name?: { message?: string };
    taxId?: { message?: string };
    activityDomainId?: { message?: string };
    activityDomainOther?: { message?: string };
  }[] | undefined;

  return (
    <div className="w-full flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h3 className="heading-h3-semibold text-(--font-primary)">{t('projects.wizard.step2.title')}</h3>
        {/* Plain caption rather than a coloured pill: as a chip it competed with the heading
            beside it for the same attention. */}
        {projectTypeLabel && (
          <span className="body-sm-regular text-(--font-secondary)">
            {t('projects.wizard.step2.projectChip', { name: projectTypeLabel })}
          </span>
        )}
        {/* The whole step is optional (office decision 17/09); this says so, and still asks for
            as much as the coordinator has, because the committee decides on what it is shown. */}
        <p className="body-sm-regular text-(--font-secondary)">{t('projects.wizard.step2.optionalHint')}</p>
      </div>

      {expenses.fields.map((field, index) => {
        const frequency = watch(`expenses.${index}.frequency`);
        return (
          <div key={field.id} className="flex flex-col gap-4 border border-(--border-main) rounded-lg p-4">
            {/* Three columns now that "סיבת הוצאה" is gone: cost / frequency / valid-until. */}
            <div className="grid grid-cols-3 max-md:grid-cols-1 gap-4">
              <div className="flex flex-col gap-1">
                <label htmlFor={`expense-amount-${index}`} className="body-sm-semibold text-(--font-secondary)">
                  {t('projects.wizard.step2.amountLabel')}
                </label>
                <input
                  id={`expense-amount-${index}`}
                  type="number"
                  min={0}
                  className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                  placeholder={t('projects.wizard.step2.amountPlaceholder')}
                  // The field starts EMPTY rather than at 0: a pre-filled zero has to be deleted
                  // before typing and reads as a real amount. An empty input maps back to
                  // undefined, which the "at least one expense" rule treats as unfilled.
                  {...register(`expenses.${index}.amount`, {
                    setValueAs: (value) =>
                      value === '' || value === null || Number.isNaN(Number(value)) ? undefined : Number(value),
                  })}
                />
                {expenseErrors?.[index]?.amount && (
                  <p className="text-red-500 body-sm-regular">{expenseErrors[index]!.amount!.message}</p>
                )}
              </div>

              <div className="flex flex-col gap-1">
                <span className="body-sm-semibold text-(--font-secondary)">
                  {t('projects.wizard.step2.frequencyLabel')}
                </span>
                {/* Pill toggle, as in the design: חודשי / מצטבר. Stretched to the column so it
                    lines up with the inputs beside it instead of floating in a wide empty cell. */}
                <div className="inline-flex border border-(--border-main) rounded-full p-1" role="group">
                  {[ProjectExpenseFrequency.Monthly, ProjectExpenseFrequency.Cumulative].map((option) => (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={frequency === option}
                      onClick={() => setValue(`expenses.${index}.frequency`, option)}
                      className={`flex-1 px-4 py-1 rounded-full body-sm-regular transition-colors ${
                        frequency === option ? 'bg-(--surface-primary) text-white' : 'text-(--font-secondary)'
                      }`}
                    >
                      {t(`projects.frequency.${option}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor={`expense-valid-until-${index}`} className="body-sm-semibold text-(--font-secondary)">
                  {t('projects.wizard.step2.validUntilLabel')}
                </label>
                <input
                  id={`expense-valid-until-${index}`}
                  type="date"
                  dir="ltr"
                  className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                  {...register(`expenses.${index}.validUntil`)}
                />
              </div>
            </div>

            {expenses.fields.length > 1 && (
              <button
                type="button"
                className="body-sm-semibold text-red-500 self-start underline"
                onClick={() => expenses.remove(index)}
              >
                {t('projects.wizard.step2.removeExpense')}
              </button>
            )}
          </div>
        );
      })}

      <Button variant="outline" onClick={() => expenses.append({ ...emptyExpense })} className="self-start">
        + {t('projects.wizard.step2.addExpense')}
      </Button>

      <div className="flex flex-col gap-3 border-t border-(--border-main) pt-5">
        <h4 className="body-lr-semibold text-(--font-primary)">{t('projects.wizard.step2.documentsTitle')}</h4>
        {/* An empty section used to be a hairline and a lone "+" button, which read as a screen
            that had failed to load rather than as one with nothing in it yet. */}
        {documents.fields.length === 0 && (
          <p className="body-sm-regular text-(--font-secondary)">{t('projects.wizard.step2.noDocuments')}</p>
        )}
        {documents.fields.map((field, index) => (
          <FileUpload
            key={field.id}
            placeholder={t('projects.wizard.step2.addDocument')}
            value={watch(`documents.${index}.file`)}
            onChange={(file) => setValue(`documents.${index}.file`, file)}
            customDescription={{
              description: watch(`documents.${index}.description`) ?? '',
              onDescriptionChange: (description) => setValue(`documents.${index}.description`, description),
              onRemove: () => documents.remove(index),
            }}
          />
        ))}
        <Button
          variant="outline"
          onClick={() => documents.append({ file: null, description: '' })}
          className="self-start"
        >
          + {t('projects.wizard.step2.addDocument')}
        </Button>
      </div>

      <div className="flex flex-col gap-3 border-t border-(--border-main) pt-5">
        <h4 className="body-lr-semibold text-(--font-primary)">{t('projects.wizard.step2.suppliersTitle')}</h4>
        {suppliers.fields.length === 0 && (
          <p className="body-sm-regular text-(--font-secondary)">{t('projects.wizard.step2.noSuppliers')}</p>
        )}

        {suppliers.fields.map((field, index) => {
          const isOtherDomain = watch(`suppliers.${index}.activityDomainId`) === OTHER_OPTION_ID;
          return (
            <div key={field.id} className="flex flex-col gap-4 border border-(--border-main) rounded-lg p-4">
              <ExistingSupplierPicker suppliers={fundSuppliers ?? []} onPick={(supplier) => fillSupplier(index, supplier)} />
              <div className="grid grid-cols-3 max-md:grid-cols-1 gap-4">
                <div className="flex flex-col gap-1">
                  <label htmlFor={`supplier-name-${index}`} className="body-sm-semibold text-(--font-secondary)">
                    {t('projects.newSupplier.nameLabel')}
                  </label>
                  <input
                    id={`supplier-name-${index}`}
                    type="text"
                    className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                    placeholder={t('projects.newSupplier.namePlaceholder')}
                    {...register(`suppliers.${index}.name`)}
                  />
                  {supplierErrors?.[index]?.name && (
                    <p className="text-red-500 body-sm-regular">{supplierErrors[index]!.name!.message}</p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor={`supplier-taxid-${index}`} className="body-sm-semibold text-(--font-secondary)">
                    {t('projects.newSupplier.taxIdLabel')}
                  </label>
                  <input
                    id={`supplier-taxid-${index}`}
                    type="text"
                    className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                    placeholder={t('projects.newSupplier.taxIdPlaceholder')}
                    {...register(`suppliers.${index}.taxId`, {
                      // A tax id the fund already knows fills the rest of the row; any other one is a
                      // new supplier, so a previous pick no longer applies.
                      onChange: (e) => {
                        const known = findSupplierByTaxId(fundSuppliers, e.target.value);
                        if (known) {
                          fillSupplier(index, known);
                        } else {
                          setValue(`suppliers.${index}.supplierId`, null);
                        }
                      },
                    })}
                  />
                  {supplierErrors?.[index]?.taxId && (
                    <p className="text-red-500 body-sm-regular">{supplierErrors[index]!.taxId!.message}</p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor={`supplier-domain-${index}`} className="body-sm-semibold text-(--font-secondary)">
                    {t('projects.newSupplier.activityDomainLabel')}
                  </label>
                  <select
                    id={`supplier-domain-${index}`}
                    className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                    {...register(`suppliers.${index}.activityDomainId`, {
                      setValueAs: (value) => (value === '' ? null : Number(value)),
                    })}
                  >
                    <option value="">{t('projects.newSupplier.activityDomainPlaceholder')}</option>
                    {activityDomains.map((domain) => (
                      <option key={domain.id} value={domain.id}>{domain.name}</option>
                    ))}
                    <option value={OTHER_OPTION_ID}>{t('projects.newSupplier.activityDomainOther')}</option>
                  </select>
                  {supplierErrors?.[index]?.activityDomainId && (
                    <p className="text-red-500 body-sm-regular">{supplierErrors[index]!.activityDomainId!.message}</p>
                  )}
                </div>
              </div>

              {isOtherDomain && (
                <div className="flex flex-col gap-1">
                  <input
                    type="text"
                    className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                    placeholder={t('projects.newSupplier.activityDomainOtherPlaceholder')}
                    {...register(`suppliers.${index}.activityDomainOther`)}
                  />
                  {supplierErrors?.[index]?.activityDomainOther && (
                    <p className="text-red-500 body-sm-regular">{supplierErrors[index]!.activityDomainOther!.message}</p>
                  )}
                </div>
              )}

              <button
                type="button"
                className="body-sm-semibold text-red-500 self-start underline"
                onClick={() => suppliers.remove(index)}
              >
                {t('projects.wizard.step2.removeSupplier')}
              </button>
            </div>
          );
        })}

        <Button variant="outline" onClick={() => suppliers.append({ ...emptySupplier })} className="self-start">
          + {t('projects.wizard.step2.addSupplier')}
        </Button>
      </div>
    </div>
  );
};

export default ProjectExpensesStep;
